import { CURRENCY } from "store/apis/balanceAPI/values"
import { INewBalance } from "store/apis/balanceAPI/balance.types"
import { IMongoDocument } from "utils/types"

export enum BOSS_LEAGUE {
  LEAGUE_1 = 1,
  LEAGUE_2 = 2,
  LEAGUE_3 = 3
}

export interface IBossStats {
  strength: {
    level: number
    minDamage: number
    maxDamage: number
  }
  defense: {
    level: number
    value: number
  }
  penetration: {
    level: number
    value: number
  }
  energyRegen: {
    level: number
    value: number
  }
  luck: {
    level: number
    value: number
  }
  accuracyLevel: number
  dodgeLevel: number
  maxEnergyLevel: number
  upgradesUsedToday: number
  upgradesRemaining: number
  maxDailyUpgrades: number
  dailyUpgradesResetAt: string | null
  darkRespecUpgradesRemaining: number
  clanBonuses: {
    minDamage: number
    maxDamage: number
    energyRegenBonus: number
    luckBonus: number
    defenseBonus: number
    penetrationBonus: number
  }
  apparelBonuses: {
    minDamage: number
    maxDamage: number
    energyRegen: number
    luck: number
    defense: number
    penetration: number
    accuracy: number
    dodge: number
  }
  elixirBonuses: {
    minDamage: number
    maxDamage: number
    luck: number
    defense: number
    penetration: number
  }
  altarBonuses: {
    strength: number
    luck: number
    defense: number
    penetration: number
    accuracy: number
    dodge: number
  }
  current: {
    minDamage: number
    maxDamage: number
    energyRegen: number
    luck: number
    defense: number
    penetration: number
    accuracy: number
    dodge: number
  }
  base: {
    minDamage: number
    maxDamage: number
    energyRegen: number
    luck: number
    defense: number
    penetration: number
    accuracy: number
    dodge: number
  }
  league: BOSS_LEAGUE
  totalDamage: number
  position: number
  cycleArenaScore: number
  cyclePosition: number
}

export type BossStatType =
  | "strength"
  | "energyRegen"
  | "luck"
  | "defense"
  | "penetration"
  | "accuracy"
  | "dodge"

export const DARK_RESPEC_POINTS_CURRENCY = "darkRespecPoints" as const
export type DarkRespecPointsCurrency = typeof DARK_RESPEC_POINTS_CURRENCY

export interface IUpgradeCost {
  currency: CURRENCY | DarkRespecPointsCurrency
  value: number
}

export type RuneType = "speed" | "endurance" | "luck"

export interface IBossUpgradeCost {
  cost: IUpgradeCost
  runeRequired: boolean
  runeLevel?: number
  runeCount?: number
  runeType?: RuneType
  runeImgSrc?: string
  canUpgrade: boolean
  reason?: string
  nextLevelValue?: {
    minDamage?: number
    maxDamage?: number
    energyRegenValue?: number
    luckValue?: number
    defenseValue?: number
    penetrationValue?: number
    accuracyValue?: number
    dodgeValue?: number
  }
}

export interface IBossEnergyStatus {
  current: number
  max: number
  secondsPerUnit: number
  secondsToNextUnit: number
  secondsToFull: number
  nextEnergyAt: string | null
  clanRegenBonus: number
}

export type BossEnergyKind = "pve" | "pvp"

export interface IBossEnergy {
  pve: IBossEnergyStatus
  pvp: IBossEnergyStatus
}

export interface IBossEnergyUpgradeCost {
  cost: IUpgradeCost
  currentMax: number
  nextMax?: number
  canUpgrade: boolean
  reason?: string
}

export interface IBossAttackResult {
  damage: number
  isCrit: boolean
  isMiss: boolean
  isPenetration: boolean
  drops: {
    gold: number
    ton: number
  }
  energy: IBossEnergyStatus
}

export interface IBossEnterResult {
  pendingGold: number
  pendingTon: number
}

export interface IBossCollectDropRequest {
  currency: CURRENCY
  amount: number
}

export interface IBossCollectDropResult {
  collected: number
  remainingGold: number
  remainingTon: number
}

export interface IBossHealth {
  health: number
  defense: number
  defenseIncrement: number
  defenseIncrementIntervalHours: number
}

export interface IBossUpgradeResult {
  newStats: IBossStats
  cost: IUpgradeCost
  runeConsumed: boolean
  newBalance: INewBalance
  nextUpgradeCost: IBossUpgradeCost
}

export interface IBossEnergyUpgradeResult {
  link?: string
  newLevel?: number
  newMax?: number
  cost?: IUpgradeCost
  energyFilled?: boolean
  newBalance?: INewBalance
}

export interface IBossLogType {
  boss_death: {
    seasonIndex: number
  }
  boss_restart: null
  vampirism_activated: {
    durationMs: number
    endsAt: string
  }
  berserker_ended: null
  berserker_activated: {
    userId: string
    cost: number
    durationMs: number
    endsAt: string
  }
  vampirism_ended: null
  recharge: {
    healthBefore: number
    healthAfter: number
    amount: number
  }
  enhanced_defense_activated: {
    baseDefense: number
    boostedDefense: number
    multiplier: number
    durationMs: number
    endsAt: string
  }
  enhanced_defense_ended: {
    baseDefense: number
    multiplier: number
    reason: "expired" | "manual"
  }
}

export type BossLogType = keyof IBossLogType

export type IBossLog<T extends BossLogType = BossLogType> = IMongoDocument & {
  type: T
  details: IBossLogType[T]
}

export interface IBossLogs {
  items: IBossLog[]
  total: number
}

export interface IBossAbilities {
  vampirism: {
    isActive: boolean
    endAt: string | null
    durationMs: number
  }
  enhancedDefense: {
    isActive: boolean
    endAt: string | null
    durationMs: number
    multiplier: number
  }
  recharge: {
    amount: number
  }
  berserker: {
    isActive: boolean
    endAt: string | null
    cost: number
    durationMs: number
  }
}

export interface IBossDarkRespecInfo {
  darkRespecPoints: number
  darkRespecUpgradesRemaining: number
  nextDarkRespecAvailableAt: string | null
  darkRespecIntervalMs: number
  previewRefundPoints: number
  previewLevelsReset: number
  darkRespecsUsed: number
  nextResetCost: { value: number; currency: string } | null
}

export interface IBossDarkRespecResult {
  refundPoints: number
  levelsReset: number
  nextDarkRespecAvailableAt: string | Date | null
  darkRespecPoints: number
  darkRespecUpgradesRemaining: number
  nextResetCost?: { value: number; currency: string } | null
  cost?: { value: number; currency: string } | null
  newStats?: IBossStats
  newBalance?: INewBalance
}

export interface IBossActivateBerserkerResult {
  success: boolean
  berserkerEndAt: string
  goldSpent: number
}

export interface IPvpAttackRequest {
  targetUserId: string
  isHit: boolean
}

export interface IPvpAttackResult {
  damage: number
  actualDamage: number
  isCrit: boolean
  isPenetration: boolean
  isDodge: boolean
  isMiss: boolean
  attackerNewTotalDamage: number
  targetNewTotalDamage: number
  attackerNewPosition: number
  targetNewPosition: number
  attackerEnergy: number
}

export type AttackLogType = "boss_attack" | "pvp_attack" | "pvp_received"
export type CombinedLogType = "boss_event" | "attack"

export interface IArenaOpponent {
  userId: string
  username: string | null
  dogname: string
  avatar: string | null
  league: number
  position: number
  totalDamage: number
  stats: IBossStats
}

export interface IArenaMatch {
  targetUserId: string
  matchedAt: string
}

export interface IFindOpponentResult {
  match: IArenaMatch | null
  opponent: IArenaOpponent | null
  attackerEnergy: number
}

export type ArenaAttackOutcome = "HIT" | "MISS_SLIDER" | "MISS_DODGE"

export interface IArenaAttackRequest {
  isHit: boolean
}

export interface IArenaAttackResult {
  result: ArenaAttackOutcome
  damage: number
  isCrit: boolean
  pointsStolen: number
  attackerNewTotalDamage: number
  targetNewTotalDamage: number
  attackerNewPosition: number
  targetNewPosition: number
  attackerEnergy: number
}

export interface ICombinedLogItem {
  _id: string
  logType: CombinedLogType
  createdAt: string
  eventType?: BossLogType
  eventDetails?: Record<string, unknown> | null
  attackType?: AttackLogType
  damage?: number
  actualDamageDealt?: number | null
  isCrit?: boolean
  isPenetration?: boolean
  isDodge?: boolean | null
  isMiss?: boolean | null
  attackerId?: string
  attackerName?: string
  targetId?: string | null
  targetName?: string | null
  goldDrop?: number
  tonDrop?: number
}

export interface ICombinedLogs {
  items: ICombinedLogItem[]
  total: number
}
